import { UsageWidget } from "@/components/account/UsageWidget";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiClient } from "@/lib/api/client";
import type { ManagedApiKeyPayload } from "@/lib/api/types";
import * as React from "react";

export default function AccountApiKeysPage() {
  const [keys, setKeys] = React.useState<ManagedApiKeyPayload[]>([]);
  const [loadingKeys, setLoadingKeys] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [keyError, setKeyError] = React.useState<string | null>(null);
  const [keyMessage, setKeyMessage] = React.useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = React.useState<Record<string, string>>({});

  const loadKeys = React.useCallback(async () => {
    setLoadingKeys(true);
    setKeyError(null);
    try {
      const data = await apiClient.getApiKeys();
      setKeys(data);
    } catch (error) {
      setKeyError(error instanceof Error ? error.message : "Unable to load API keys.");
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  React.useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const onReveal = async (id: string) => {
    setActionLoading(`reveal:${id}`);
    setKeyError(null);
    setKeyMessage(null);
    try {
      const data = await apiClient.revealApiKey(id);
      setRevealedKeys((current) => ({
        ...current,
        [id]: data.apiKey,
      }));
    } catch (error) {
      setKeyError(error instanceof Error ? error.message : "Unable to reveal key.");
    } finally {
      setActionLoading(null);
    }
  };

  const onRotate = async (id?: string) => {
    setActionLoading(`rotate:${id ?? "primary"}`);
    setKeyError(null);
    setKeyMessage(null);
    try {
      const data = await apiClient.rotateApiKey(id);
      setRevealedKeys((current) => ({
        ...current,
        [data.replacement.id]: data.replacementApiKey,
      }));
      setKeyMessage("API key rotated. Save the new key now; it will only be shown here in full once.");
      await loadKeys();
    } catch (error) {
      setKeyError(error instanceof Error ? error.message : "Unable to rotate key.");
    } finally {
      setActionLoading(null);
    }
  };

  return renderAccountApiKeysPage({
    keys,
    loadingKeys,
    actionLoading,
    keyError,
    keyMessage,
    revealedKeys,
    loadKeys,
    onReveal,
    onRotate,
  });
}

type AccountApiKeysPageViewProps = {
  keys: ManagedApiKeyPayload[];
  loadingKeys: boolean;
  actionLoading: string | null;
  keyError: string | null;
  keyMessage: string | null;
  revealedKeys: Record<string, string>;
  loadKeys: () => Promise<void>;
  onReveal: (id: string) => Promise<void>;
  onRotate: (id?: string) => Promise<void>;
};

function renderAccountApiKeysPage({
  keys,
  loadingKeys,
  actionLoading,
  keyError,
  keyMessage,
  revealedKeys,
  loadKeys,
  onReveal,
  onRotate,
}: AccountApiKeysPageViewProps) {
  return (
    <div className="space-y-6">
      <UsageWidget />

      <Card>
        <CardHeader>
          <CardTitle>Account API keys</CardTitle>
          <CardDescription>
            TMS login uses email/password. These keys are for external API integrations in your own apps.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void loadKeys()} disabled={loadingKeys}>
              {loadingKeys ? "Refreshing..." : "Refresh"}
            </Button>
            <Button onClick={() => void onRotate()} disabled={actionLoading !== null}>
              Rotate primary key
            </Button>
          </div>

          {keyError ? <p className="text-sm text-destructive">{keyError}</p> : null}
          {keyMessage ? <p className="text-sm text-muted-foreground">{keyMessage}</p> : null}

          <div className="space-y-3">
            {keys.length === 0 && !loadingKeys ? (
              <p className="text-sm text-muted-foreground">No API keys found for this account.</p>
            ) : null}
            {keys.map((key) => (
              <div key={key.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{key.maskedKey}</p>
                    <p className="text-xs text-muted-foreground">
                      Created: {key.createdAt ? new Date(key.createdAt).toLocaleString() : "Unknown"}
                      {key.isPrimary ? " · Primary" : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void onReveal(key.id)}
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === `reveal:${key.id}` ? "Revealing..." : "Reveal"}
                    </Button>
                    <Button size="sm" onClick={() => void onRotate(key.id)} disabled={actionLoading !== null}>
                      {actionLoading === `rotate:${key.id}` ? "Rotating..." : "Rotate"}
                    </Button>
                  </div>
                </div>

                {revealedKeys[key.id] ? (
                  <div className="mt-3 rounded bg-muted p-2">
                    <p className="text-xs font-medium">Full key (copy now):</p>
                    <code className="break-all text-xs">{revealedKeys[key.id]}</code>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
